/** Resetting a forgotten password - card C-B, gate 1. One route, two jobs.
 *
 * **No link in the address: ask.** A person who has forgotten their password gives an address and is
 * told, in one sentence, that if it belongs to an account a link has been sent. **That sentence is
 * the same whether or not the address does** - the API answers 202 with identical bytes either way
 * (`test_a_reset_request_answers_with_identical_bytes`), so this screen has nothing to distinguish
 * and must not invent a way to. A "no account with that address" here would turn the form into an
 * address validator, which `/sign-in` refuses to be in the same way.
 *
 * **A link in the address (`#token=...`): choose.** The token travels in the URL *fragment*, which
 * is not sent to the server, not in a `Referer` and not in an access log. This screen reads it once,
 * **removes it from the address bar** so it does not sit in the tab's history entry or get copied
 * with the page, and holds it in memory only - never in storage, never in the mutation cache
 * (`gcTime: 0`). It is a bearer credential for the account.
 *
 * **One message for every unusable link.** The API gives an unknown, used, replaced and expired link
 * the same 404 and body, so this screen has one sentence and renders nothing from the response, for
 * `Join.tsx`'s reason. What *is* told apart is a password that is too short - the link was not
 * spent, so the remedy is a longer one - and a request that never completed, which is not the
 * person's fault and has a different remedy.
 *
 * **Setting a password does not sign anyone in**, and the last step says so: they sign in like
 * anybody else, and an Admin or Approver still passes a second factor. The reset signs every other
 * session out, which is the point of doing it when a password may be stolen, and is said.
 */

import { useEffect, useRef, useState } from "react";

import { useCompletePasswordReset, useRequestPasswordReset } from "../api/passwordReset";
import { AuthCard, AuthField, AuthNotice, AuthPage } from "../components/AuthLayout";
import { useDocumentTitle } from "../shell/documentTitle";
import styles from "../components/AuthLayout.module.css";

/** The server's rule, mirrored so it can be said before somebody types and checked before a
 * single-use link is spent. **The server is the authority** (`passwords.MIN_LENGTH`) and refuses
 * regardless; see `Join.tsx` on how a drifted copy fails (with the same notice, never silently). */
const MIN_PASSWORD_LENGTH = 12;

/** The link as it arrives: `/reset-password#token=...`. Empty when there is none. */
function tokenFromFragment(): string {
  const hash = typeof window === "undefined" ? "" : window.location.hash;
  const found = /^#token=([A-Za-z0-9_-]+)$/.exec(hash);
  return found?.[1] ?? "";
}

type Stage = "ask" | "asked" | "choose" | "done";
/** The id a field-level notice carries, so the field it is about can point at it. */
const NOTICE_ID = "reset-notice";

type Notice = "refused" | "too_weak" | "mismatch" | "unreachable" | null;

export function ResetPasswordScreen() {
  // Read once, at the first render, and held. `useState`'s initialiser rather than an effect so the
  // first paint is already the right step and there is no flash of the wrong form.
  const [token, setToken] = useState(tokenFromFragment);
  const [stage, setStage] = useState<Stage>(() => (token ? "choose" : "ask"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [notice, setNotice] = useState<Notice>(null);

  useDocumentTitle(stage === "ask" || stage === "asked" ? "Reset your password" : "Choose a new password");

  const request = useRequestPasswordReset();
  const complete = useCompletePasswordReset();

  // Take the credential out of the address bar as soon as it has been read. `replaceState` and not
  // a router navigation: the router would add an entry, and the point is that none keeps the token.
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  const inFlight = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const shown = useRef<Stage>(stage);
  useEffect(() => {
    if (shown.current !== stage) {
      shown.current = stage;
      heading.current?.focus();
    }
  }, [stage]);

  async function submitAsk(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    setNotice(null);
    inFlight.current = true;
    try {
      await request.mutateAsync(email.trim());
      setStage("asked");
    } catch {
      // The request did not complete. Nothing is known about the address either way.
      setNotice("unreachable");
    } finally {
      inFlight.current = false;
    }
  }

  async function submitChoose(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    setNotice(null);
    // Both checked **before** anything is sent: the link is single use, and a request that spends it
    // on a password the person did not mean to type cannot be taken back.
    if (password !== confirm) {
      setNotice("mismatch");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setNotice("too_weak");
      return;
    }
    inFlight.current = true;
    try {
      const result = await complete.mutateAsync({ token, newPassword: password });
      if (result.status === "too_weak") {
        // Kept, not cleared: it is the thing being corrected. The server refused before spending.
        setNotice("too_weak");
        return;
      }
      if (result.status === "refused") {
        setPassword("");
        setConfirm("");
        setToken("");
        setStage("ask");
        setNotice("refused");
        return;
      }
      // Done with: nothing on this page needs them again, and a password left in state is a
      // password in a React devtools panel.
      setToken("");
      setPassword("");
      setConfirm("");
      complete.reset();
      setStage("done");
    } catch {
      setNotice("unreachable");
    } finally {
      inFlight.current = false;
    }
  }

  /** The fields a notice is about, so a screen reader that lands on one hears why. */
  const passwordNotice = notice === "mismatch" || notice === "too_weak" ? NOTICE_ID : undefined;

  const message =
    notice === null ? null : (
      <>
        {notice === "refused" ? (
          <AuthNotice testId="reset-refused">
            <strong>That link could not be used.</strong> It may have expired, already been used, or
            been replaced by a newer one. Enter your address to get a new link.
          </AuthNotice>
        ) : null}

        {notice === "mismatch" ? (
          <AuthNotice id={NOTICE_ID} testId="reset-mismatch">
            The two passwords are not the same. Nothing has been sent - type them again.
          </AuthNotice>
        ) : null}

        {notice === "too_weak" ? (
          <AuthNotice id={NOTICE_ID} testId="reset-too-weak">
            <strong>
              That password is too short - it must be at least {MIN_PASSWORD_LENGTH} characters.
            </strong>{" "}
            Nothing has been changed and your link has not been used.
          </AuthNotice>
        ) : null}

        {notice === "unreachable" ? (
          <AuthNotice testId="reset-unreachable">
            <strong>EmitIQ could not be reached.</strong> Nothing has been changed. Try again in a
            moment.
          </AuthNotice>
        ) : null}
      </>
    );

  return (
    <AuthPage>
      {stage === "ask" ? (
        <AuthCard title="Reset your password" stage="ask" headingRef={heading} notice={message}>
          <p className={styles.prose}>
            Enter the email address you sign in with. If it belongs to an account, we will send a
            link to choose a new password. The link works once and expires in one hour.
          </p>
          <form className={styles.form} onSubmit={(event) => void submitAsk(event)}>
            <AuthField label="Email address">
              <input
                name="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </AuthField>
            <button className={styles.primary} type="submit" disabled={request.isPending}>
              {request.isPending ? "Sending…" : "Send me a link"}
            </button>
          </form>
          <div className={styles.aside}>
            <p>
              <a href="/sign-in">Back to sign in</a>
            </p>
          </div>
        </AuthCard>
      ) : null}

      {stage === "asked" ? (
        <AuthCard title="Check your email" stage="asked" headingRef={heading} notice={message}>
          {/* **The same sentence for every address**, and it claims nothing it cannot know. */}
          <p className={styles.prose} data-testid="reset-asked">
            If that address belongs to an account, a link to choose a new password is on its way. It
            can take a few minutes to arrive, works once and expires in one hour. If nothing
            arrives after ten minutes, check your spam folder, then ask again.
          </p>
          <div className={styles.aside}>
            <p>
              <a href="/sign-in">Back to sign in</a>
            </p>
          </div>
        </AuthCard>
      ) : null}

      {stage === "choose" ? (
        <AuthCard
          title="Choose a new password"
          stage="choose"
          headingRef={heading}
          notice={message}
        >
          <form className={styles.form} onSubmit={(event) => void submitChoose(event)}>
            <AuthField label="New password">
              <input
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={MIN_PASSWORD_LENGTH}
                aria-invalid={passwordNotice ? true : undefined}
                aria-describedby={passwordNotice}
                required
              />
            </AuthField>
            <AuthField label="Type the new password again">
              <input
                name="password_again"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-invalid={notice === "mismatch" || undefined}
                aria-describedby={notice === "mismatch" ? NOTICE_ID : undefined}
                required
              />
            </AuthField>
            <p className={styles.prose}>
              At least {MIN_PASSWORD_LENGTH} characters - length is what makes a password hard to
              guess, and there are no rules about capitals or symbols. This signs you out everywhere
              you are signed in.
            </p>
            <button className={styles.primary} type="submit" disabled={complete.isPending}>
              {complete.isPending ? "Saving…" : "Set my password"}
            </button>
          </form>
        </AuthCard>
      ) : null}

      {stage === "done" ? (
        <AuthCard
          title="Your password has been changed"
          stage="done"
          headingRef={heading}
          notice={message}
        >
          <p className={styles.prose}>
            You were signed out everywhere. Sign in with the new password. If your role needs a
            second factor, you will be asked for it as usual - resetting a password does not change
            that.
          </p>
          <a className={styles.primary} href="/sign-in" data-testid="reset-sign-in">
            Go to sign in
          </a>
        </AuthCard>
      ) : null}
    </AuthPage>
  );
}
